import Section from './Section.jsx';
import { diningPhoto, googleDiningPhoto, parseAttribution } from '../lib/dining-photos.js';

// A monogram stands in until a real photo lands. Unique per restaurant, so a
// photo-less list reads as a designed menu rather than a row of identical fork
// icons — and a listed-but-missing file falls back to it via onError instead
// of showing a broken image.
function initialOf(name) {
  const cleaned = String(name || '').replace(/^the\s+/i, '').trim();
  return (cleaned[0] || '·').toUpperCase();
}

// Photo source priority: a hand-placed licensed local file wins, then the
// restaurant's Google Place photo (served via our key-hiding proxy), then
// nothing (monogram). `google` marks when attribution must be shown.
function thumbSource(pick) {
  const local = diningPhoto(pick.name);
  if (local) return { src: local, google: false };
  const google = googleDiningPhoto(pick.photoRef);
  if (google) return { src: google, google: true };
  return { src: null, google: false };
}

function Thumb({ pick }) {
  const { src } = thumbSource(pick);
  // cream-200, a step up from the card's cream-100, so the monogram tile still
  // reads as a distinct frame now that the card is not white.
  return (
    <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-md bg-cream-200 sm:h-[72px] sm:w-[72px]">
      <span
        aria-hidden="true"
        className="absolute inset-0 flex items-center justify-center font-display text-2xl font-semibold text-cream-600"
      >
        {initialOf(pick.name)}
      </span>
      {src && (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={(e) => {
            // A photo that fails to load (404'd local file, Google miss) reveals
            // the monogram underneath instead of a broken image.
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
    </div>
  );
}

// Google requires visible attribution wherever a Place photo is shown. Collect
// the unique contributors across the picks that actually use a Google photo.
function googleCredits(picks) {
  const seen = new Set();
  const out = [];
  for (const p of picks) {
    const { google } = thumbSource(p);
    if (!google || !p.photoAttribution) continue;
    const credit = parseAttribution(p.photoAttribution);
    if (!credit || seen.has(credit.text)) continue;
    seen.add(credit.text);
    out.push(credit);
  }
  return out;
}

export default function DiningTonight({ picks }) {
  if (!picks?.length) return null;
  const credits = googleCredits(picks);
  return (
    <Section id="dining" title="Dining tonight" eyebrow="Where to eat">
      <div className="space-y-3">
        {picks.map((p, i) => (
          <div
            key={i}
            // Neutral card: cream-100, borderless — the same surface the weather
            // stats and forecast strip use. This was white-on-border, the only
            // pure-white surface on a warm-paper page.
            className="flex items-start gap-3.5 rounded-lg bg-cream-100 p-3"
          >
            <Thumb pick={p} />
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-baseline justify-between gap-3">
                <h3 className="font-display text-base font-semibold text-navy-900">{p.name}</h3>
                <span className="shrink-0 text-xs uppercase tracking-wider text-navy-500">
                  {p.neighborhood}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-navy-700">{p.blurb}</p>
            </div>
          </div>
        ))}
      </div>
      {credits.length > 0 && (
        <p className="mt-3 text-[11px] leading-relaxed text-cream-600">
          Photos via Google
          {credits.length > 0 && ' · '}
          {credits.map((c, i) => (
            <span key={c.text}>
              {i > 0 && ', '}
              {c.href ? (
                <a
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-navy-700"
                >
                  {c.text}
                </a>
              ) : (
                c.text
              )}
            </span>
          ))}
        </p>
      )}
    </Section>
  );
}
